import React, { useState, useRef, useEffect } from 'react';
import { View, Text, TextInput, TouchableOpacity, StyleSheet, Platform, Animated, Easing, ScrollView } from 'react-native';
import { BlurView } from 'expo-blur';
import { FL, FONT } from './floodlight/theme';

// Floodlight skin (the Optimizer, v1.71): rounded glass fields with a hairline,
// a near-black glass list and the purple accent on the picked option. Only
// tone="paper" uses it; the default (Classic) skin below is unchanged.
const WEB_FL = Platform.OS === 'web';
const PAPER = {
    label: { color: FL.muted, fontSize: 11, fontWeight: '600', letterSpacing: 1, textTransform: 'uppercase', fontFamily: FONT },
    input: { backgroundColor: FL.paper2, borderColor: FL.hair2, borderWidth: 1, borderRadius: 14, color: FL.ink, fontFamily: FONT, minHeight: 48 },
    placeholder: FL.muted,
    clear: { backgroundColor: FL.stage, borderRadius: 999, borderWidth: 1, borderColor: FL.hair2 },
    clearText: { color: FL.onStage },
    icon: { color: FL.ink2 },
    container: { borderRadius: 16, ...(WEB_FL ? { boxShadow: '0 20px 50px rgba(0,0,0,0.6)' } : {}) },
    inner: {
        backgroundColor: FL.paper, borderColor: FL.hair2, borderWidth: 1, borderRadius: 16,
        ...(WEB_FL ? { backgroundImage: 'linear-gradient(rgba(255,255,255,0.06), rgba(255,255,255,0.06))' } : {}),
    },
    option: { backgroundColor: 'transparent', borderBottomColor: FL.hair },
    optionText: { color: FL.ink, fontFamily: FONT },
    selected: { backgroundColor: FL.greenWash },
    selectedText: { color: FL.greenInk, fontWeight: '700' },
    more: { color: FL.muted, fontFamily: FONT },
};

/**
 * AutocompleteDropdown - A hybrid text input with dropdown functionality
 * Users can type to search/filter options OR click to see the full dropdown
 */
// Stable default: a fresh [] each render would re-run the filter effect forever.
const NO_OPTIONS = [];

const AutocompleteDropdown = ({ 
    label, 
    value, 
    onValueChange, 
    options = NO_OPTIONS, 
    placeholder = 'Type or select...',
    style,
    disabled = false,
    zIndex = 1,
    allowFreeText = false,
    tone = 'dark',
    minInputWidth = 200,
    maxVisible = 200,
    emptyText = 'No matches found',
    // (typed, options) => the options to list, in order. Default: contains the typed text.
    matchOptions,
}) => {
    const paper = tone === 'paper' ? PAPER : null;
    const [inputValue, setInputValue] = useState('');
    const [showDropdown, setShowDropdown] = useState(false);
    const [filteredOptions, setFilteredOptions] = useState(options);
    // True once the user types after opening the field. Free-text fields echo
    // each keystroke back as `value`, so comparing text to value can't tell a
    // search from the committed pick.
    const [typed, setTyped] = useState(false);
    const dropdownRef = useRef(null);
    const inputRef = useRef(null);
    const valueRef = useRef(value);
    valueRef.current = value;
    
    // Update input value when value prop changes
    useEffect(() => {
        if (value && value !== 'all') {
            setInputValue(value);
        } else {
            setInputValue('');
        }
    }, [value]);
    
    // Filter options by what was typed. Opening a filled field ("All Games")
    // still lists every option until the user types.
    useEffect(() => {
        if (inputValue.trim() === '' || !typed) {
            setFilteredOptions(options);
        } else if (matchOptions) {
            setFilteredOptions(matchOptions(inputValue, options));
        } else {
            const filtered = options.filter(option =>
                option.toLowerCase().includes(inputValue.toLowerCase())
            );
            setFilteredOptions(filtered);
        }
    }, [inputValue, options, typed, matchOptions]);
    
    // Handle input change
    const handleInputChange = (text) => {
        setInputValue(text);
        setTyped(true);
        setShowDropdown(true);
        
        if (allowFreeText) {
            // Always push the typed value up to the parent
            onValueChange(text);
        } else {
            // If exact match, select it
            const exactMatch = options.find(opt => opt.toLowerCase() === text.toLowerCase());
            if (exactMatch) {
                onValueChange(exactMatch);
            }
        }
    };
    
    // Handle option selection
    const handleSelectOption = (option) => {
        setInputValue(option);
        setTyped(false);
        onValueChange(option);
        setShowDropdown(false);
    };
    
    // Handle focus
    const handleFocus = () => {
        setTyped(false);
        setShowDropdown(true);
    };
    
    // Handle blur (with delay to allow click on dropdown)
    const handleBlur = () => {
        if (allowFreeText && inputValue.trim() !== '') {
            onValueChange(inputValue.trim());
        }
        setTimeout(() => {
            setShowDropdown(false);
            // A partial pick ("Bask") in a list-only field goes back to the committed value.
            if (!allowFreeText) {
                const v = valueRef.current;
                setInputValue(v && v !== 'all' ? v : '');
            }
        }, 200);
    };
    
    // Clear selection
    const handleClear = () => {
        setInputValue('');
        onValueChange('');
        setShowDropdown(false);
    };
    
    // Calculate effective z-index - when dropdown is open, boost it significantly
    const effectiveZIndex = showDropdown ? zIndex + 10000 : zIndex;
    
    return (
        <View style={[styles.container, style, { zIndex: effectiveZIndex, position: 'relative' }]}>
            {!!label && <Text style={[styles.label, paper && paper.label]}>{label}</Text>}
            
            <View style={[styles.inputContainer, { zIndex: effectiveZIndex }]}>
                <TextInput
                    ref={inputRef}
                    style={[styles.input, { minWidth: minInputWidth }, paper && paper.input, disabled && styles.inputDisabled]}
                    value={inputValue}
                    onChangeText={handleInputChange}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    placeholder={placeholder}
                    placeholderTextColor={paper ? paper.placeholder : 'rgba(255,255,255,0.4)'}
                    editable={!disabled}
                    accessibilityLabel={label || placeholder}
                    {...(paper ? { dataSet: { flInput: '' } } : {})}
                />
                
                {inputValue && !disabled ? (
                    <TouchableOpacity 
                        style={[styles.clearButton, paper && paper.clear]}
                        onPress={handleClear}
                        activeOpacity={0.7}
                        accessibilityRole="button"
                        accessibilityLabel={`Clear ${label || 'selection'}`}
                    >
                        <Text style={[styles.clearButtonText, paper && paper.clearText]}>×</Text>
                    </TouchableOpacity>
                ) : (
                    <TouchableOpacity 
                        style={styles.dropdownIcon}
                        onPress={() => !disabled && setShowDropdown(!showDropdown)}
                        activeOpacity={0.7}
                    >
                        <Text style={[styles.dropdownIconText, paper && paper.icon]}>{showDropdown ? '▲' : '▼'}</Text>
                    </TouchableOpacity>
                )}
            </View>
            
            {showDropdown && filteredOptions.length > 0 && (
                <View style={[styles.dropdownContainer, paper && paper.container, { zIndex: effectiveZIndex + 1 }]}>
                    <View style={[styles.dropdownInner, paper && paper.inner]}>
                        <ScrollView 
                            style={[styles.dropdownScroll, paper && { backgroundColor: 'transparent' }]}
                            nestedScrollEnabled={true}
                            showsVerticalScrollIndicator={true}
                        >
                            {/* Long lists (3,000+ players) render the first maxVisible; typing narrows them. */}
                            {filteredOptions.slice(0, maxVisible).map((option, index) => (
                                <TouchableOpacity
                                    key={`${index}:${option}`}
                                    style={[
                                        styles.dropdownOption,
                                        paper && paper.option,
                                        option === value && (paper ? paper.selected : styles.dropdownOptionSelected)
                                    ]}
                                    onPress={() => handleSelectOption(option)}
                                    activeOpacity={0.7}
                                >
                                    <Text style={[
                                        styles.dropdownOptionText,
                                        paper && paper.optionText,
                                        option === value && (paper ? paper.selectedText : styles.dropdownOptionTextSelected)
                                    ]}>
                                        {option}
                                    </Text>
                                </TouchableOpacity>
                            ))}
                            {filteredOptions.length > maxVisible && (
                                <Text style={[styles.moreText, paper && paper.more]}>
                                    {filteredOptions.length - maxVisible} more. Type to narrow.
                                </Text>
                            )}
                        </ScrollView>
                    </View>
                </View>
            )}
            
            {showDropdown && filteredOptions.length === 0 && inputValue.trim() !== '' && (
                <View style={[styles.dropdownContainer, paper && paper.container, { zIndex: effectiveZIndex + 1 }]}>
                    <View style={[styles.dropdownInner, paper && paper.inner]}>
                        <View style={styles.noResultsContainer}>
                            <Text style={[styles.noResultsText, paper && paper.more]}>
                                {typeof emptyText === 'function' ? emptyText(inputValue.trim()) : emptyText}
                            </Text>
                        </View>
                    </View>
                </View>
            )}
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        position: 'relative',
        width: '100%',
    },
    label: {
        color: 'rgba(255,255,255,0.8)',
        fontSize: 14,
        fontWeight: '600',
        marginBottom: 8,
    },
    inputContainer: {
        position: 'relative',
        flexDirection: 'row',
        alignItems: 'center',
    },
    input: {
        flex: 1,
        backgroundColor: '#1a1a1a',
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.3)',
        borderRadius: 8,
        paddingVertical: 14,
        paddingHorizontal: 16,
        paddingRight: 40,
        color: 'rgba(255,255,255,0.95)',
        fontSize: 15,
        fontWeight: '500',
        ...(Platform.OS === 'web' ? {
            outlineStyle: 'none',
        } : {}),
    },
    inputDisabled: {
        opacity: 0.5,
    },
    clearButton: {
        position: 'absolute',
        right: 12,
        width: 24,
        height: 24,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#3a3a3a',
        borderRadius: 12,
    },
    clearButtonText: {
        color: 'rgba(255,255,255,0.7)',
        fontSize: 18,
        fontWeight: '600',
    },
    dropdownIcon: {
        position: 'absolute',
        right: 12,
        width: 24,
        height: 24,
        alignItems: 'center',
        justifyContent: 'center',
    },
    dropdownIconText: {
        color: 'rgba(255,255,255,0.6)',
        fontSize: 12,
    },
    dropdownContainer: {
        position: 'absolute',
        top: '100%',
        left: 0,
        right: 0,
        marginTop: 4,
        maxHeight: 280,
        borderRadius: 8,
        overflow: 'hidden',
        zIndex: 9999,
        ...(Platform.OS === 'web' ? {
            boxShadow: '0 12px 48px rgba(0,0,0,0.8)',
        } : {
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 4 },
            shadowOpacity: 0.5,
            shadowRadius: 12,
            elevation: 12,
        }),
    },
    dropdownInner: {
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.25)',
        borderRadius: 8,
        overflow: 'hidden',
        backgroundColor: '#0d0d0d',
    },
    dropdownScroll: {
        maxHeight: 280,
        backgroundColor: '#0d0d0d',
    },
    dropdownOption: {
        paddingVertical: 12,
        paddingHorizontal: 16,
        borderBottomWidth: 1,
        borderBottomColor: 'rgba(255,255,255,0.08)',
        backgroundColor: '#0d0d0d',
    },
    dropdownOptionSelected: {
        backgroundColor: '#2d1f4e',
    },
    dropdownOptionText: {
        color: 'rgba(255,255,255,0.9)',
        fontSize: 14,
        fontWeight: '500',
    },
    dropdownOptionTextSelected: {
        color: '#e9d5ff',
        fontWeight: '600',
    },
    noResultsContainer: {
        paddingVertical: 16,
        paddingHorizontal: 16,
        alignItems: 'center',
    },
    moreText: {
        paddingVertical: 12,
        paddingHorizontal: 16,
        color: 'rgba(255,255,255,0.5)',
        fontSize: 13,
    },
    noResultsText: {
        color: 'rgba(255,255,255,0.5)',
        fontSize: 14,
        fontStyle: 'italic',
    },
});

export default AutocompleteDropdown;

